import { DirectiveBinding, ObjectDirective } from 'vue'
// 指令配置类型
export interface LazyImageOptions {
  src: string
  loading?: string
  error?: string
  observerOptions?: IntersectionObserverInit
  blurAmount?: string
  transition?: string
}

// 指令绑定值类型
export type LazyImageBindingValue = string | LazyImageOptions

const defaultOptions = {
  threshold: 0.1,
  rootMargin: '0px 0px 100px 0px',
  blurAmount: '8px',
  transition: 'filter .2s ease-out'
} satisfies Partial<LazyImageOptions>

const lazyImage = {
  mounted: (el: HTMLImageElement, binding: DirectiveBinding<LazyImageBindingValue>) => {
    const { value, modifiers } = binding
    const options = getOptions(value, modifiers)

    // 设置占位图
    if (options.loading) {
      el.src = options.loading
    }

    // 设置模糊效果
    if (modifiers.blur || options.blurAmount) {
      applyBlurEffect(el, options.blurAmount || defaultOptions.blurAmount)
    }

    // 初始化观察者
    initObserver(el, options)
  },

  updated: (el: HTMLImageElement, binding: DirectiveBinding<LazyImageBindingValue>) => {
    const newSrc = typeof binding.value === 'string' ? binding.value : binding.value.src
    if (el.src !== newSrc) {
      cleanupObserver(el)
      lazyImage.mounted(el, binding)
    }
  },

  unmounted: (el: HTMLImageElement) => {
    cleanupObserver(el)
  }
} as const satisfies ObjectDirective<HTMLImageElement, LazyImageBindingValue>

// 获取合并后的配置选项
function getOptions(value: LazyImageBindingValue, modifiers: DirectiveBinding['modifiers']): LazyImageOptions {
  const baseOptions = typeof value === 'string' ? { src: value } : value

  return {
    ...defaultOptions,
    blurAmount: modifiers.blur ? defaultOptions.blurAmount : undefined,
    ...baseOptions,
    observerOptions: {
      threshold: defaultOptions.threshold,
      rootMargin: defaultOptions.rootMargin,
      ...baseOptions.observerOptions
    }
  }
}

// 应用模糊效果
function applyBlurEffect(el: HTMLImageElement, blurAmount: string) {
  el.style.filter = `blur(${blurAmount})`
  el.style.transition = defaultOptions.transition
}

// 初始化 IntersectionObserver
function initObserver(el: HTMLImageElement, options: LazyImageOptions) {
  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        loadImage(el, options)
        observer.unobserve(el)
      }
    })
  }, options.observerOptions)

  el._lazyImageObserver = observer
  observer.observe(el)
}

// 加载图片
function loadImage(el: HTMLImageElement, options: LazyImageOptions) {
  el.src = options.src

  el.onload = () => {
    el.style.filter = 'none'
    el.dispatchEvent(new CustomEvent('lazy-loaded', { detail: { src: options.src } }))
  }

  el.onerror = () => {
    if (options.error) {
      el.src = options.error
    }
    el.dispatchEvent(new CustomEvent('lazy-error', { detail: { src: options.src } }))
  }
}

// 清理观察者
function cleanupObserver(el: HTMLImageElement & { _lazyImageObserver?: IntersectionObserver }) {
  if (el._lazyImageObserver) {
    el._lazyImageObserver.disconnect()
    delete el._lazyImageObserver
  }
}

export default lazyImage
